(() => {
  const rootSelector = '[data-ae-policy-page]';
  const activeControllers = new WeakMap();

  function currentHashKey() {
    return decodeURIComponent(window.location.hash || '').replace(/^#/, '').trim();
  }

  function normalizePathname(value) {
    if (!value) {
      return '';
    }

    try {
      const url = new URL(value, window.location.origin);
      return url.pathname.replace(/\/+$/, '') || '/';
    } catch (error) {
      return '';
    }
  }

  function setPanelInactive(panel, inactive) {
    panel.hidden = inactive;

    if ('inert' in panel) {
      panel.inert = inactive;
    }

    if (inactive) {
      panel.setAttribute('inert', '');
    } else {
      panel.removeAttribute('inert');
    }
  }

  function scrollToPolicyTop(root) {
    const offset = Number(root.dataset.scrollOffset || 0);
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const top = window.scrollY + root.getBoundingClientRect().top - offset;

    window.scrollTo({
      top: Math.max(0, Math.round(top)),
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
  }

  function initPolicyPage(root) {
    if (!root || activeControllers.has(root)) {
      return;
    }

    const tabs = Array.from(root.querySelectorAll('[data-ae-policy-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-ae-policy-panel]'));
    const currentTitle = root.querySelector('[data-ae-policy-current-title]');

    if (!tabs.length || !panels.length) {
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    activeControllers.set(root, controller);

    const keys = panels.map((panel) => panel.dataset.policyKey).filter(Boolean);
    const keySet = new Set(keys);
    const configuredDefault = root.dataset.defaultPolicyKey;
    const defaultPolicyKey = keySet.has(configuredDefault) ? configuredDefault : keys[0];
    const keyByPath = new Map();

    tabs.forEach((tab) => {
      const pathname = normalizePathname(tab.dataset.policyUrl);

      if (pathname && !keyByPath.has(pathname)) {
        keyByPath.set(pathname, tab.dataset.policyKey);
      }
    });

    function normalizeKey(key) {
      return keySet.has(key) ? key : defaultPolicyKey;
    }

    function keyFromLocation() {
      const pathKey = keyByPath.get(normalizePathname(window.location.href));

      if (keySet.has(pathKey)) {
        return pathKey;
      }

      const hashKey = currentHashKey();

      if (keySet.has(hashKey)) {
        return hashKey;
      }

      return defaultPolicyKey;
    }

    function urlForPolicy(activeKey) {
      const tab = tabs.find((candidate) => candidate.dataset.policyKey === activeKey);
      const policyUrl = tab ? tab.dataset.policyUrl : '';

      if (!policyUrl) {
        return `#${encodeURIComponent(activeKey)}`;
      }

      const nextUrl = new URL(policyUrl, window.location.origin);
      const currentUrl = new URL(window.location.href);

      if (!nextUrl.search) {
        nextUrl.search = currentUrl.search;
      }

      nextUrl.hash = '';

      return `${nextUrl.pathname}${nextUrl.search}`;
    }

    function updatePolicyUrl(activeKey, mode) {
      if (!window.history || !window.history.pushState || !window.history.replaceState) {
        return;
      }

      const nextUrl = urlForPolicy(activeKey);
      const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

      if (!nextUrl || nextUrl === currentUrl) {
        return;
      }

      const state = { aePolicyKey: activeKey };

      if (mode === 'push') {
        window.history.pushState(state, '', nextUrl);
      } else {
        window.history.replaceState(state, '', nextUrl);
      }
    }

    function activatePolicy(key, options = {}) {
      const activeKey = normalizeKey(key);
      let nextTitle = '';

      tabs.forEach((tab) => {
        const selected = tab.dataset.policyKey === activeKey;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;

        if (selected) {
          nextTitle = tab.dataset.policyTitle || '';
        }
      });

      panels.forEach((panel) => {
        const inactive = panel.dataset.policyKey !== activeKey;
        const contact = panel.querySelector('[data-ae-policy-contact]');

        setPanelInactive(panel, inactive);

        if (!inactive && !nextTitle) {
          nextTitle = panel.dataset.policyTitle || '';
        }

        if (contact) {
          if (inactive) {
            contact.removeAttribute('id');
          } else {
            contact.id = 'contact';
          }
        }
      });

      if (currentTitle && nextTitle) {
        currentTitle.textContent = nextTitle;
      }

      if (options.updateUrl) {
        updatePolicyUrl(activeKey, options.updateUrl);
      }

      if (options.scrollToTop) {
        window.requestAnimationFrame(() => scrollToPolicyTop(root));
      }
    }

    function focusAndActivateTab(tab) {
      if (!tab) {
        return;
      }

      tab.focus();
      activatePolicy(tab.dataset.policyKey, {
        updateUrl: 'push',
        scrollToTop: true
      });
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', (event) => {
        event.preventDefault();

        activatePolicy(tab.dataset.policyKey, {
          updateUrl: 'push',
          scrollToTop: true
        });
      }, { signal });
    });

    const tablist = root.querySelector('[role="tablist"]');

    if (tablist) {
      tablist.addEventListener('keydown', (event) => {
        const currentIndex = tabs.indexOf(document.activeElement);

        if (currentIndex < 0) {
          return;
        }

        let nextIndex = currentIndex;

        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
          nextIndex = (currentIndex + 1) % tabs.length;
        } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        } else if (event.key === 'Home') {
          nextIndex = 0;
        } else if (event.key === 'End') {
          nextIndex = tabs.length - 1;
        } else {
          return;
        }

        event.preventDefault();
        focusAndActivateTab(tabs[nextIndex]);
      }, { signal });
    }

    window.addEventListener('hashchange', () => {
      activatePolicy(keyFromLocation(), {
        updateUrl: false,
        scrollToTop: true
      });
    }, { signal });

    window.addEventListener('popstate', () => {
      activatePolicy(keyFromLocation(), {
        updateUrl: false,
        scrollToTop: true
      });
    }, { signal });

    activatePolicy(keyFromLocation(), {
      updateUrl: false,
      scrollToTop: false
    });
  }

  function destroyPolicyPage(root) {
    const controller = activeControllers.get(root);

    if (!controller) {
      return;
    }

    controller.abort();
    activeControllers.delete(root);
  }

  function initAll(scope = document) {
    scope.querySelectorAll(rootSelector).forEach(initPolicyPage);
  }

  function destroyAll(scope = document) {
    scope.querySelectorAll(rootSelector).forEach(destroyPolicyPage);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyAll(event.target);
  });
})();
